import { useState, useRef } from 'react'
import { CHASSIS } from '../../lib/caseReport/template.js'

// The page number's place belongs to the master page, not to this tool. One
// source of truth: a chassis change moves the Builder's number and this stamp
// together, which is the whole point of template.js.
const FOOT = CHASSIS.footer
const FRAME_RIGHT = CHASSIS.contentCols[CHASSIS.contentCols.length - 1]

function PngToPdfTab() {
  const [pages,       setPages]       = useState([])
  const [dragIdx,     setDragIdx]     = useState(null)
  const [dragOverIdx, setDragOverIdx] = useState(null)
  const [preview,     setPreview]     = useState(null)
  const [generating,  setGenerating]  = useState(false)
  const [progress,    setProgress]    = useState({ cur: 0, total: 0 })
  const [quality,     setQuality]     = useState(0.92)
  const fileInputRef = useRef(null)

  function addFiles(fileList) {
    const pngs = [...fileList].filter(f => f.name.toLowerCase().endsWith('.png'))
    if (!pngs.length) return
    const incoming = pngs
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map(f => ({ id: crypto.randomUUID(), file: f, name: f.name, thumbUrl: URL.createObjectURL(f) }))
    setPages(prev => [...prev, ...incoming])
  }

  function removeFile(idx) {
    setPages(prev => {
      URL.revokeObjectURL(prev[idx].thumbUrl)
      return prev.filter((_, i) => i !== idx)
    })
  }

  function movePage(from, to) {
    if (from == null || to == null || from === to) return
    setPages(prev => {
      const arr = [...prev]
      const [item] = arr.splice(from, 1)
      arr.splice(to, 0, item)
      return arr
    })
  }

  function sortAZ() {
    setPages(prev => [...prev].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })))
  }
  function sortZA() {
    setPages(prev => [...prev].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true })))
  }

  function onZoneDrop(e) {
    e.preventDefault()
    addFiles(e.dataTransfer.files)
  }

  // A4 @ 200 DPI max — preserves source resolution up to the cap, then scales down proportionally.
  function compressToJpeg(file, quality = 0.92) {
    const MAX_W = 1654  // 210 mm × (200 / 25.4) ≈ 1654 px
    const MAX_H = 2339  // 297 mm × (200 / 25.4) ≈ 2339 px
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let w = img.naturalWidth  || MAX_W
        let h = img.naturalHeight || MAX_H
        // Scale down only if source exceeds the cap, preserving aspect ratio
        const scale = Math.min(1, MAX_W / w, MAX_H / h)
        w = Math.round(w * scale)
        h = Math.round(h * scale)
        const canvas = document.createElement('canvas')
        canvas.width  = w
        canvas.height = h
        canvas.getContext('2d').drawImage(img, 0, 0, w, h)
        resolve(canvas.toDataURL('image/jpeg', quality))
      }
      img.onerror = reject
      img.src = url
    })
  }

  async function generate() {
    if (!pages.length || generating) return
    setGenerating(true)
    setProgress({ cur: 0, total: pages.length })
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

      // Mulish for the page-number stamp — the Osteo3d house face, the same one
      // the website and every other document use. This stamped Questrial until
      // 2026-08-01, which was the only place in the portal that did.
      let pageNumFont = 'helvetica'
      try {
        const fontRes    = await fetch('/fonts/Mulish-Regular.ttf')
        const fontBuf    = await fontRes.arrayBuffer()
        const fontBase64 = btoa(String.fromCharCode(...new Uint8Array(fontBuf)))
        doc.addFileToVFS('Mulish-Regular.ttf', fontBase64)
        doc.addFont('Mulish-Regular.ttf', 'Mulish', 'normal')
        pageNumFont = 'Mulish'
      } catch {
        // fall back to helvetica if font load fails
      }

      const total = pages.length
      for (let i = 0; i < total; i++) {
        setProgress({ cur: i + 1, total })
        if (i > 0) doc.addPage()
        const imgData = await compressToJpeg(pages[i].file, quality)
        doc.addImage(imgData, 'JPEG', 0, 0, 210, 297)
        // The page number goes in the MASTER's own page-number box, read from the
        // chassis rather than typed here. These were hardcoded 167 / 291.5 / 9pt,
        // which predate the master page of 26 Sep 2026 and sit 10.29 mm LEFT of
        // where it puts the number: the blanking rect started at 167 and erased
        // the tail of `contactus@Osteo3d.com` (footer.contactInkRight 171.20),
        // then the number printed over the email line. Team, 5 Oct 2026.
        //
        // The rect spans the master's page-number x to the content frame's right
        // rule, so it covers whatever number the incoming sheet already carries
        // and stops 6.09 mm clear of the email's right ink edge.
        const em = (FOOT.pageNumberSize * 25.4) / 72
        doc.setFillColor(255, 255, 255)
        doc.rect(FOOT.pageNumberX, FOOT.pageNumberBaseline - em, FRAME_RIGHT - FOOT.pageNumberX, em * 1.4, 'F')
        doc.setFont(pageNumFont, 'normal')
        doc.setFontSize(FOOT.pageNumberSize)
        doc.setTextColor(0, 0, 0)
        const label = `Page ${String(i + 1).padStart(2, '0')} of ${String(total).padStart(2, '0')}`
        doc.text(label, FOOT.pageNumberX, FOOT.pageNumberBaseline)
      }
      doc.save('CaseReport.pdf')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">PNG → PDF</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Combine A4 PNG pages into a single PDF. Drag thumbnails to reorder — page numbers are stamped automatically.
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700"
        >
          + Add Pages
        </button>
        <input
          ref={fileInputRef} type="file" accept=".png,image/png" multiple className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {/* Drop zone */}
      <div
        onDrop={onZoneDrop}
        onDragOver={e => e.preventDefault()}
        className="min-h-[300px] border-2 border-dashed border-blue-300 rounded-xl bg-yellow-50 p-4"
      >
        {pages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 select-none">
            <svg className="w-12 h-12 mb-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="font-medium text-gray-500">Drop PNG files here</p>
            <p className="text-sm mt-1 text-gray-400">
              or click{' '}
              <span
                className="text-brand-600 underline cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >Add Pages</span>{' '}above
            </p>
            <p className="text-xs mt-3 text-gray-300">Order of files can be changed by drag and drop</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {pages.map((p, idx) => (
              <div
                key={p.id}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={e => { e.preventDefault(); setDragOverIdx(idx) }}
                onDrop={e => { e.preventDefault(); e.stopPropagation(); movePage(dragIdx, idx); setDragIdx(null); setDragOverIdx(null) }}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                className={[
                  'relative flex flex-col items-center cursor-grab active:cursor-grabbing w-[140px] select-none rounded-lg transition-all',
                  dragOverIdx === idx && dragIdx !== idx ? 'ring-2 ring-brand-400 scale-105' : '',
                  dragIdx === idx ? 'opacity-40' : '',
                ].join(' ')}
              >
                {/* action icons */}
                <div className="absolute top-1 left-1 right-1 flex justify-between px-0.5 z-10">
                  <button
                    title="Preview"
                    onClick={() => setPreview(p)}
                    className="bg-white/80 hover:bg-white rounded p-0.5 text-blue-500 shadow"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                    </svg>
                  </button>
                  <button
                    title="Remove"
                    onClick={() => removeFile(idx)}
                    className="bg-white/80 hover:bg-white rounded p-0.5 text-red-500 shadow"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a1 1 0 00-1-1h-4a1 1 0 00-1 1m-2 0h10" />
                    </svg>
                  </button>
                </div>
                {/* thumbnail */}
                <img
                  src={p.thumbUrl} alt={p.name} draggable={false}
                  className="w-[130px] h-[184px] object-cover border border-gray-200 rounded shadow-sm bg-white mt-1"
                />
                {/* filename */}
                <span className="mt-1 text-xs text-gray-600 truncate w-[130px] text-center">{p.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <button
          onClick={sortAZ} disabled={pages.length < 2} title="Sort A→Z"
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 4h13M3 8h9M3 12h5m8 0l3-3m0 0l3 3m-3-3v12" />
          </svg>
          A→Z
        </button>
        <button
          onClick={sortZA} disabled={pages.length < 2} title="Sort Z→A"
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 4h13M3 8h9M3 12h5m8 8l3 3m0 0l3-3m-3 3V9" />
          </svg>
          Z→A
        </button>
        {pages.length > 0 && (
          <span className="text-sm text-gray-500 ml-1">
            {pages.length} page{pages.length !== 1 ? 's' : ''}
          </span>
        )}
        <div className="flex-1" />
        {/* Quality slider */}
        <div className="flex items-center gap-2 mr-3">
          <span className="text-xs text-gray-500 whitespace-nowrap">Quality</span>
          <input
            type="range" min="0.70" max="0.99" step="0.01"
            value={quality}
            onChange={e => setQuality(parseFloat(e.target.value))}
            disabled={generating}
            className="w-28 accent-orange-500 disabled:opacity-40"
          />
          <span className="text-xs font-medium text-gray-700 w-8 tabular-nums">{Math.round(quality * 100)}%</span>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {quality < 0.80 ? '~1–2 MB/pg' : quality < 0.90 ? '~2–3 MB/pg' : quality < 0.95 ? '~3–5 MB/pg' : quality < 0.98 ? '~5–7 MB/pg' : '~8–10 MB/pg'}
          </span>
        </div>
        {generating && (
          <span className="text-sm text-gray-400">
            Processing {progress.cur} / {progress.total}…
          </span>
        )}
        <button
          onClick={generate}
          disabled={pages.length === 0 || generating}
          className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
        >
          {generating ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Generating…
            </>
          ) : 'Generate PDF'}
        </button>
      </div>

      {/* Lightbox preview */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => setPreview(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] p-2" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreview(null)}
              className="absolute -top-8 right-0 text-white text-2xl font-bold leading-none"
            >
              ✕
            </button>
            <img
              src={preview.thumbUrl} alt={preview.name}
              className="max-w-full max-h-[85vh] rounded shadow-xl"
            />
            <p className="text-white text-sm text-center mt-2 opacity-70">{preview.name}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PngToPdfTab
